import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * HoverCard — opens a styled preview when the trigger is hovered (mouse)
 * or focused (keyboard). Use for non-critical contextual previews: avatar →
 * profile card, link → page summary, badge → definition.
 *
 * NOT a substitute for Tooltip — Tooltip is for plain-text labels.
 * HoverCard is for rich content (avatars, paragraphs, action rows).
 *
 * Built on Radix HoverCard so it gets the right open / close delays, keyboard
 * focus handling, and dismissal semantics for free.
 * On touch/coarse-pointer devices the trigger toggles by click. On pointer
 * devices it keeps the Radix hover/focus behavior.
 */

type HoverCardClickMode = boolean | "touch";

interface HoverCardClickContextValue {
  readonly openOnClick: HoverCardClickMode;
  readonly toggleOpen: () => void;
}

const HoverCardClickContext = React.createContext<HoverCardClickContextValue | null>(null);

function shouldOpenOnClick(mode: HoverCardClickMode): boolean {
  if (mode === true) return true;
  if (mode === false) return false;
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

export interface HoverCardProps
  extends React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root> {
  /** Enables click/tap toggle behavior. Defaults to touch/coarse-pointer devices only. */
  openOnClick?: HoverCardClickMode;
}

export function HoverCard({
  children,
  defaultOpen,
  onOpenChange,
  open,
  openOnClick = "touch",
  ...props
}: HoverCardProps) {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(Boolean(defaultOpen));
  const resolvedOpen = isControlled ? open : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const context = React.useMemo(
    () => ({
      openOnClick,
      toggleOpen: () => setOpen(!resolvedOpen),
    }),
    [openOnClick, resolvedOpen, setOpen],
  );

  return (
    <HoverCardClickContext.Provider value={context}>
      <HoverCardPrimitive.Root open={resolvedOpen} onOpenChange={setOpen} {...props}>
        {children}
      </HoverCardPrimitive.Root>
    </HoverCardClickContext.Provider>
  );
}

export const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(function HoverCardTrigger({ onClick, ...props }, ref) {
  const context = React.useContext(HoverCardClickContext);
  return (
    <HoverCardPrimitive.Trigger
      ref={ref}
      onClick={(event) => {
        if (context && shouldOpenOnClick(context.openOnClick)) {
          event.preventDefault();
          context.toggleOpen();
        }
        onClick?.(event);
      }}
      {...props}
    />
  );
});
HoverCardTrigger.displayName = "HoverCardTrigger";
export const HoverCardPortal = HoverCardPrimitive.Portal;

export const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(function HoverCardContent({ align = "center", className, sideOffset = 8, ...props }, ref) {
  return (
    <HoverCardPrimitive.Portal>
      <HoverCardPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cx("cw-v2-theme cw-v2-hover-card__content", className)}
        {...props}
      />
    </HoverCardPrimitive.Portal>
  );
});
HoverCardContent.displayName = "HoverCardContent";

export const HoverCardArrow = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Arrow>
>(function HoverCardArrow({ className, ...props }, ref) {
  return (
    <HoverCardPrimitive.Arrow
      ref={ref}
      width={12}
      height={6}
      className={cx("cw-v2-hover-card__arrow", className)}
      {...props}
    />
  );
});
HoverCardArrow.displayName = "HoverCardArrow";
